import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { APIEndpoints, baseURL } from "@/utils/APIEndpoints";
import { setToken } from "@/store/authSlice";
import { Mutex } from "async-mutex";

const mutex = new Mutex();

export const authAPI = createApi({
  reducerPath: "authAPI",
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState, endpoint }) => {
      headers.set("apiKey", sessionStorage.getItem("apiKey"));
      if (endpoint === "getNewRefreshToken") {
        headers.set("username", getState().authReducer?.username);
        headers.set("password", getState().authReducer?.password);
      }
      return headers;
    },
  }),
  keepUnusedDataFor: 300,
  endpoints: (builder) => ({
    // Login API
    login: builder.query({
      query: ({ username, password, newPassword, confirmPassword }) => ({
        url: APIEndpoints.login,
        method: "GET",
        headers: {
          username: username,
          password: password,
          newPassword: newPassword,
          confirmPassword: confirmPassword,
        },
      }),
    }),
    getNewRefreshToken: builder.query({
      query: () => APIEndpoints.newRefreshToken,
    }),
    // Get PreLogin Keys/Secrets
    getSettings: builder.query({
      query: () => APIEndpoints.settings,
    }),
    // Retrieve Security Questions using Email ID
    retrieveCustomOperatorId: builder.query({
      query: ({ emailId }) => ({
        url: APIEndpoints.retrieveCustomOperatorId,
        method: "GET",
        params: {
          emailId: emailId,
        },
      }),
    }),
    // Retrieve Operator ID using security questions
    retrieveOperatorId: builder.mutation({
      query: (payload) => ({
        url: APIEndpoints.retrieveOperatorId,
        method: "POST",
        body: payload,
      }),
    }),
    // Retrieve Accessor ID and Security Questions
    retrieveAccessorId: builder.query({
      query: (accessorId) => ({
        url: APIEndpoints.retrieveAccessorId,
        method: "GET",
        params: { accessorId: accessorId },
      }),
    }),
    // Get Operator details using Custom User ID
    forgotPassword: builder.query({
      query: ({ operatorId }) => ({
        url: APIEndpoints.forgotPassword,
        method: "GET",
        params: {
          operatorId: operatorId,
        },
      }),
    }),
    // Change Password using security questions
    changePassword: builder.mutation({
      query: (payload) => ({
        url: APIEndpoints.changePassword,
        headers: {
          operatorId: payload.customOperatorId,
        },
        method: "POST",
        body: payload,
      }),
    }),
    // Change Expired Password without any security questions
    changeExpiredPassword: builder.mutation({
      query: (payload) => ({
        url: APIEndpoints.changeExpiredPassword,
        headers: {
          operatorId: payload.customOperatorId,
        },
        method: "POST",
        body: payload,
      }),
    }),
    // Change Expired Password without any security questions
    setupPassword: builder.mutation({
      query: (payload) => ({
        url: APIEndpoints.setupPassword,
        headers: {
          operatorId: payload.operatorId,
        },
        method: "POST",
        body: payload,
      }),
    }),
    // Get ALL Access Profiles for enrollment
    getAccessProfilesForEnrollment: builder.query({
      query: () => ({
        url: APIEndpoints.getAccessProfilesForEnrollment,
        method: "GET",
      }),
    }),
    // Get ALL Insurance Companies
    getInsuranceCompaniesForEnrollment: builder.query({
      query: () => ({
        url: APIEndpoints.getInsuranceCompaniesForEnrollment,
        method: "GET",
      }),
      providesTags: ["insuranceCompany"],
    }),
    // Get US States
    getStatesForEnrollment: builder.query({
      query: () => ({
        url: APIEndpoints.getStatesForEnrollment,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    // Get All/Some Product Manufacturers
    getProductManufacturers: builder.query({
      query: ({ insuranceCompanyId }) => ({
        url: APIEndpoints.getProductManufacturers,
        method: "GET",
        params: {
          insuranceCompanyId: insuranceCompanyId,
        },
      }),
    }),
    // Process Enrollment/ Create new operator
    processEnrollment: builder.mutation({
      query: (payload) => ({
        url: APIEndpoints.processEnrollment,
        method: "POST",
        body: payload,
      }),
    }),
  }),
});

const baseQueryWithToken = fetchBaseQuery({
  baseUrl: baseURL,
  prepareHeaders: (headers, { getState }) => {
    headers.set("apiKey", sessionStorage.getItem("apiKey"));
    headers.set(
      "Authorization",
      `Bearer ${getState()?.authReducer?.token?.access_token}`
    );
    if (!headers.has("operatorId")) {
      headers.set("operatorId", getState()?.commonReducer?.accessorDetails?.id);
    }
    return headers;
  },
});

const baseQueryWithRefreshToken = fetchBaseQuery({
  baseUrl: baseURL,
  prepareHeaders: (headers, { getState }) => {
    headers.set("apiKey", sessionStorage.getItem("apiKey"));
    headers.set("refresh_token", getState()?.authReducer?.token?.refresh_token);
    return headers;
  },
});

const baseQueryWithHandler = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQueryWithToken(args, api, extraOptions);

  if (result?.error?.status == 401 || result?.status == 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshToken = await baseQueryWithRefreshToken(
          APIEndpoints.refreshToken,
          api,
          extraOptions
        );

        await api.dispatch(setToken(refreshToken.data));
        result = await baseQueryWithToken(args, api, extraOptions);
      } catch {
        api.dispatch(setToken({}));
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQueryWithToken(args, api, extraOptions);
    }
  }
  return result;
};

export const riccAPI = createApi({
  reducerPath: "riccAPI",
  baseQuery: baseQueryWithHandler,
  keepUnusedDataFor: 300,
  tags: [
    "contactInfo",
    "updateAccessProfile",
    "updateOperator",
    "approveProfile",
    "recordKeeper",
    "recordKeeperContact",
    "productManufacturer",
    "productManufacturerContact",
    "productManufacturerInsurances",
    "productManufacturerVehicles",
    "managementCompanies",
    "investmentVehicles",
    "insuranceCompany",
    "insuranceCompanyContact",
    "productRules",
    "productInsurances",
    "product",
  ],
  endpoints: () => ({}),
});

export const {
  useLoginQuery,
  useGetNewRefreshTokenQuery,
  useGetSettingsQuery,
  useRetrieveCustomOperatorIdQuery,
  useRetrieveOperatorIdMutation,
  useRetrieveAccessorIdQuery,
  useForgotPasswordQuery,
  useChangePasswordMutation,
  useSetupPasswordMutation,
  useChangeExpiredPasswordMutation,
  useGetAccessProfilesForEnrollmentQuery,
  useGetInsuranceCompaniesForEnrollmentQuery,
  useLazyGetProductManufacturersQuery,
  useGetProductManufacturersQuery,
  useGetStatesForEnrollmentQuery,
  useProcessEnrollmentMutation,
} = authAPI;
